import {ChangeDetectionStrategy, Component, OnInit} from '@angular/core';
import {AdminService, SchemeService} from '../../openapi-core';
import {ActivatedRoute, Router} from '@angular/router';
import {SCHEME_LINK_PARAM, SessionService} from '../../services/session.service';
import {CommonService} from '../../services/common.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {jwtDecode} from 'jwt-decode';
import {forkJoin} from 'rxjs';
import {SessionBootstrapService} from '../../services/session-bootstrap.service';
import {
  isValidHandoffId,
  PortalAuthorizationCallbackService,
} from '../../services/portal-authorization-callback.service';

interface AccessTokenClaims {
  unique_name?: string;
  role?: string | string[];
  scheme?: string;
}


@Component({
    selector: 'app-auth-callback',
    standalone: true,
    imports: [],
    templateUrl: './callback.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
})
export class CallbackComponent implements OnInit {
  private sessionPrepared = false;
  private portalHandoff = false;
  private onboardingHandoff = false;
  private handoffId: string | null = null;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private sessionService: SessionService,
    private adminService: AdminService,
    private schemeService: SchemeService,
    private sessionBootstrapService: SessionBootstrapService,
    private portalAuthorizationCallbackService: PortalAuthorizationCallbackService,
    public commonService: CommonService,
    private snackBar: MatSnackBar) {
  }

  awaitingPost = false;
  awaitingGet = false;


  ngOnInit() {
    const handoffId = this.route.snapshot.queryParamMap.get('handoffId')?.trim();
    this.onboardingHandoff = this.route.snapshot.queryParamMap.get('onboarding') === 'true';
    this.portalHandoff = true;
    if (isValidHandoffId(handoffId) && this.portalAuthorizationCallbackService.isHandoffInProgress(handoffId)) {
      return;
    }

    if (!this.portalAuthorizationCallbackService.clearCallbackLocation()) {
      return;
    }

    if (!isValidHandoffId(handoffId)) {
      this.handleHandoffFailure();
      return;
    }

    this.handoffId = handoffId;
    this.portalAuthorizationCallbackService.beginHandoff(handoffId);
    this.prepareSession();
    this.awaitingGet = true;
    this.portalAuthorizationCallbackService.exchange(handoffId).subscribe({
      next: result => result.token ? this.loadAccess(result.token) : this.handleHandoffFailure(),
      error: () => this.handleHandoffFailure(),
    });
  }

  private loadAccess(token: string): void {
    this.prepareSession();

    let claims: AccessTokenClaims;
    try {
      claims = jwtDecode<AccessTokenClaims>(token);
    } catch {
      this.snackBar.open('The access token is invalid.');
      this.endHandoff();
      this.sessionService.signOut();
      return;
    }

    if (!claims.unique_name || !claims.scheme || !claims.role) {
      this.snackBar.open('The access token is missing required claims.');
      this.endHandoff();
      this.sessionService.signOut();
      return;
    }

    this.awaitingGet = true;
    this.sessionService.token = token;
    this.sessionService.schemeId = claims.scheme;
    this.sessionService.agentRole = Array.isArray(claims.role) ? claims.role[0] : claims.role;

    forkJoin({
      user: this.adminService.apiAdminIdGet(claims.unique_name),
      scheme: this.schemeService.apiSchemeIdGet(claims.scheme),
    }).subscribe({
      next: async ({user, scheme}) => {
        this.sessionService.agent = {
          userId: user.id ?? claims.unique_name,
          userName: user.email ?? '',
        };
        if (!this.onboardingHandoff && this.openSchemeLinkDestination()) {
          return;
        }
        try {
          await this.sessionBootstrapService.initialize(scheme);
          // Replace the callback history entry, then reload the clean target so
          // third-party integrations that were suppressed on the callback can
          // initialize only after the one-time handoff cookie has been consumed.
          const navigated = this.onboardingHandoff
            ? await this.router.navigate(['/general/dashboard'], {
              queryParams: {tour: true},
              replaceUrl: true,
            })
            : await this.sessionBootstrapService.redirectToDestination(
              !this.portalHandoff,
              this.portalHandoff,
            );
          this.endHandoff();
          if (this.portalHandoff && navigated) {
            this.reloadAfterHandoff();
          }
        } catch (err) {
          this.awaitingGet = false;
          this.endHandoff();
          this.commonService.errorHandler(err);
          this.sessionService.signOut();
        }
      },
      error: err => {
        this.awaitingGet = false;
        this.endHandoff();
        this.commonService.errorHandler(err);
        this.sessionService.signOut();
      }
    });
  }


  private prepareSession(): void {
    if (this.sessionPrepared) {
      return;
    }

    this.sessionPrepared = true;
    this.sessionService.resetPortalSession();
  }

  private handleHandoffFailure(): void {
    this.awaitingGet = false;
    this.endHandoff();
    this.sessionPrepared = true;
    this.sessionService.resetPortalSession();
    this.snackBar.open('Unable to sign in. Redirecting to Auth.');
    this.sessionService.signOut();
  }

  // A scheme link opens on a fresh page. Routing to it here would start /load
  // just as the reload below fires, and /load could use up the destination first.
  private openSchemeLinkDestination(): boolean {
    const destination = sessionStorage.getItem('destination');
    const url = destination ? new URL(destination, window.location.origin) : null;
    if (url?.origin !== window.location.origin || !url.searchParams.get(SCHEME_LINK_PARAM)) {
      return false;
    }

    this.endHandoff();
    this.replaceLocation(url.href);
    return true;
  }

  private replaceLocation(url: string): void {
    window.location.replace(url);
  }

  private reloadAfterHandoff(): void {
    window.location.reload();
  }

  private endHandoff(): void {
    this.portalAuthorizationCallbackService.endHandoff(this.handoffId);
    this.handoffId = null;
  }
}
